import React from 'react';
import {useTranslation} from 'react-i18next';
import {useParams} from 'react-router-dom';

import {skipToken} from '@reduxjs/toolkit/query';

import {Button} from '@edna/components';
import {Icon, Stack} from '@edna/components/primitives';

import {Text} from 'src/components/primitives';
import {CardIcon, TrashIcon} from 'src/icons';

import {useGetPayKeeperInfoQuery, useGetTenantPayKeeperInfoQuery} from '../api';
import * as S from '../style';
import {showPayKeeperDeleteCardModal} from './PayKeeperDeleteCardModal';
import {showPayKeeperOnetimePaymentSettingsModal} from './PayKeeperSettingsModal';
import {showStripeOnetimePaymentSettingsModal} from './StripeSettingsModal';

const PayKeeperOnetimePaymentInfo = React.memo(() => {
  const {t} = useTranslation();
  const {id: tenantId} = useParams<'id'>();
  const {data: userPayKeeperInfo} = useGetPayKeeperInfoQuery(undefined, {skip: !!tenantId});
  const {data: tenantPayKeeperInfo} = useGetTenantPayKeeperInfoQuery(tenantId ?? skipToken);

  const usersPayKeeperInfo = userPayKeeperInfo ? [userPayKeeperInfo] : tenantPayKeeperInfo;

  if (!usersPayKeeperInfo?.length) {
    return null;
  }

  return (
    <>
      {usersPayKeeperInfo.map((payKeeperInfo) => (
        <Stack size="2" key={payKeeperInfo.userId}>
          <Stack direction="ROW" align="center" size="2">
            <Stack direction="ROW" align="center" size="1">
              <Icon as={CardIcon} size="20px" color="grey400" />
              <Text weight={500}>{t('BalanceReplenishment:status.savedCard')}</Text>
            </Stack>
            <Button onClick={() => showPayKeeperDeleteCardModal(payKeeperInfo.userId)} size="XS">
              <S.ButtonLeftIcon as={TrashIcon} />
              {t('BalanceReplenishment:actions.deleteCard')}
            </Button>
          </Stack>
          <S.NestedBlock>
            <Text color="grey400" size="XS">
              <Stack direction="ROW" align="center" size="2">
                <Icon as={CardIcon} size="16px" />
                {payKeeperInfo.maskedCardNumber}
              </Stack>
            </Text>
          </S.NestedBlock>
        </Stack>
      ))}
    </>
  );
});

const PayKeeperOnetimePaymentButton = React.memo(() => {
  const {t} = useTranslation();
  const {id: supportTenantId} = useParams<'id'>();

  if (supportTenantId) {
    return null;
  }

  return (
    <Button onClick={showPayKeeperOnetimePaymentSettingsModal} size="S">
      <S.ButtonLeftIcon as={CardIcon} />
      {t('BalanceReplenishment:actions.payByCard')}
    </Button>
  );
});

const StripeOnetimePaymentButton = React.memo(() => {
  const {t} = useTranslation();

  return (
    <Button onClick={showStripeOnetimePaymentSettingsModal} size="S">
      <S.ButtonLeftIcon as={CardIcon} />
      {t('BalanceReplenishment:actions.payByCard')}
    </Button>
  );
});

export {PayKeeperOnetimePaymentInfo, PayKeeperOnetimePaymentButton, StripeOnetimePaymentButton};
